import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getQuote, getCandles, getCompanyProfile, getNews,
  getFullStockData, getMultipleQuotes, searchSymbol,
} from '../services/finnhubAPI';

export function useStockData(symbol) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchData = useCallback(async () => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getFullStockData(symbol);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [symbol]);
  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, loading, error, refetch: fetchData };
}

export function useQuote(symbol, refreshInterval = 30000) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const fetchQuote = useCallback(async () => {
    if (!symbol) return;
    try {
      const data = await getQuote(symbol);
      setQuote(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [symbol]);
  useEffect(() => {
    fetchQuote();
    intervalRef.current = setInterval(fetchQuote, refreshInterval);
    return () => clearInterval(intervalRef.current);
  }, [fetchQuote, refreshInterval]);
  return { quote, loading, error };
}

export function useMultipleQuotes(symbols, refreshInterval = 30000) {
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const symbolsKey = symbols.join(',');
  const fetchAll = useCallback(async () => {
    if (!symbolsKey) return;
    const syms = symbolsKey.split(',');
    try {
      const data = await getMultipleQuotes(syms);
      setQuotes(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [symbolsKey]);
  useEffect(() => {
    setLoading(true);
    fetchAll();
    intervalRef.current = setInterval(fetchAll, refreshInterval);
    return () => clearInterval(intervalRef.current);
  }, [fetchAll, refreshInterval]);
  return { quotes, loading, error, refetch: fetchAll };
}

export function useCandles(symbol, resolution = 'D', daysBack = 30) {
  const [candles, setCandles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    getCandles(symbol, resolution, daysBack)
      .then(data => { if (!cancelled) { setCandles(data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, resolution, daysBack]);
  return { candles, loading, error };
}

export function useCompanyProfile(symbol) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    getCompanyProfile(symbol)
      .then(data => { if (!cancelled) setProfile(data); })
      .catch(err => console.error(err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol]);
  return { profile, loading };
}

export function useNews(symbol, daysBack = 7) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    getNews(symbol, daysBack)
      .then(data => { if (!cancelled) setNews(data); })
      .catch(err => console.error(err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, daysBack]);
  return { news, loading };
}

export function useIPOCalendar() {
  const [ipos, setIpos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.resolve([])
      .then(data => { if (!cancelled) { setIpos(data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  return { ipos, loading, error };
}

export function useSymbolSearch(query, debounceMs = 300) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => {
    if (!query || query.length < 1) { setResults([]); return; }
    setLoading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const data = await searchSymbol(query);
        setResults(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timerRef.current);
  }, [query, debounceMs]);
  return { results, loading };
}

// Polling-based replacement for WebSocket realtime prices.
// Fetches quotes every 30s via the Worker — no API key in the browser.
export function useRealtimePrice(symbols, refreshInterval = 30000) {
  const [prices, setPrices] = useState({});
  const symbolsKey = symbols.join(',');
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!symbolsKey) return;
    const syms = symbolsKey.split(',');

    async function fetchPrices() {
      for (const s of syms) {
        try {
          const q = await getQuote(s);
          if (q) {
            setPrices(prev => ({
              ...prev,
              [s]: { price: q.c, volume: 0, timestamp: Date.now() }
            }));
          }
        } catch (_) { /* skip failed symbols */ }
      }
    }

    fetchPrices();
    intervalRef.current = setInterval(fetchPrices, refreshInterval);
    return () => clearInterval(intervalRef.current);
  }, [symbolsKey, refreshInterval]);

  return prices;
}

// No-op: WebSocket removed, cleanup no longer needed.
export function useWebSocketCleanup() {}
